import { Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getHomeContent, homeBaseLang, homePath, unitedStatesHomePath, type HomeLocale } from "../../homeContent";
import { hongKongDownloadPath, leadFormPath } from "../../routes";
import { reportLeadEntryConversion } from "../../tracking";
import { BrandLogo } from "../BrandLogo";
import { DownloadLink } from "../DownloadLink";
import { RegionSwitcher } from "../RegionSwitcher";

export function HomeNav({ locale }: { locale: HomeLocale }) {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { nav } = getHomeContent(locale);
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const menuRef = useRef<HTMLButtonElement>(null);
  const links = [
    { label: nav.features, hash: "#capabilities" },
    { label: nav.how, hash: "#how" },
    { label: nav.why, hash: "#why" },
    { label: nav.pricing, hash: "#pricing" },
  ];
  /* 香港站右上按鈕是「下載」，直達下載頁；其他語言維持預約示範表單。 */
  const demoTo = locale === "zh-HK" ? hongKongDownloadPath("nav_download", undefined, search) : leadFormPath(homeBaseLang(locale), undefined, "home_nav_demo", search);
  const showRegion = locale === "zh-HK" || locale === "en";
  const region = locale === "zh-HK" ? "hk" : "us";
  const regionLabel = locale === "zh-HK" ? "地區" : "Region";
  useEffect(() => {
    if (!menuOpen) return;
    const outside = (event: PointerEvent) => { if (!headerRef.current?.contains(event.target as Node)) setMenuOpen(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") { setMenuOpen(false); menuRef.current?.focus(); } };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", escape); };
  }, [menuOpen]);
  return (
    <header className="ce-home-header" ref={headerRef}>
      <nav className="ce-home-nav" aria-label={nav.menu}>
        <Link to={homePath(locale, search)} className="ce-home-brand" aria-label="ClawEase" onClick={() => setMenuOpen(false)}><BrandLogo /></Link>
        <div className="ce-home-nav-links">{links.map(({ label, hash }) => <Link key={hash} to={`${homePath(locale, search)}${hash}`}>{label}</Link>)}</div>
        <div className="ce-home-nav-actions">
          {showRegion ? (
            <RegionSwitcher
              region={region}
              label={regionLabel}
              labels={{ us: locale === "zh-HK" ? "美國" : "United States", hk: locale === "zh-HK" ? "香港" : "Hong Kong" }}
              onChange={(nextRegion) => navigate(nextRegion === "us" ? `${unitedStatesHomePath}${search}` : homePath("zh-HK", search))}
              withDivider
            />
          ) : null}
          {locale === "zh-HK" ? (
            <DownloadLink className="ce-home-demo" to={demoTo} onClick={() => reportLeadEntryConversion(search)}>{nav.demo}</DownloadLink>
          ) : (
            <Link className="ce-home-demo" to={demoTo} onClick={() => reportLeadEntryConversion(search)}>{nav.demo}</Link>
          )}
          <button className="ce-home-menu-toggle" type="button" ref={menuRef} aria-label={nav.menu} aria-controls="ce-home-mobile-nav" aria-expanded={menuOpen} onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? <X size={20} /> : <Menu size={20} />}</button>
        </div>
        {menuOpen && <div className="ce-home-mobile-nav" id="ce-home-mobile-nav">{links.map(({ label, hash }) => <Link key={hash} to={`${homePath(locale, search)}${hash}`} onClick={() => setMenuOpen(false)}>{label}</Link>)}</div>}
      </nav>
    </header>
  );
}
